import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { MandateStatus } from "@/components/ui/StatusBadge";
import type { Installment } from "@/types/installment";
import { installmentService } from "@/services/apiServices";
import { formatINR } from "@/pages/retailer/wallet/data";
import { WALLET_RATES } from "@/pages/retailer/wallet/constants";

const CHARGED_STATUSES = ["COLLECTED", "SETTLEMENT_PENDING", "SETTLED", "SETTLEMENT_FAILED"];

interface Props {
  status: MandateStatus;
  refNo?: string;
  /** Override creation charge — if omitted, uses live platform rate from WALLET_RATES */
  creationCharge?: number;
  /** Override installment rate — if omitted, uses live platform rate from WALLET_RATES */
  installmentRate?: number;
  retailerWalletRow?: ReactNode;
  ledgerLink?: ReactNode;
}

function Pill({ label, className }: { label: string; className: string }) {
  return (
    <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${className}`}>
      {label}
    </span>
  );
}

/** Hook that returns live billing rates from WALLET_RATES (updates when rates change) */
function useLiveRates(creationOverride?: number, installmentOverride?: number) {
  const [rates, setRates] = useState({
    creation: creationOverride ?? WALLET_RATES.CREATION_CHARGE,
    installment: installmentOverride ?? WALLET_RATES.INSTALLMENT_CHARGE,
  });

  useEffect(() => {
    // If explicit overrides provided, use them directly
    if (creationOverride !== undefined && installmentOverride !== undefined) {
      setRates({ creation: creationOverride, installment: installmentOverride });
      return;
    }
    // Otherwise, re-read from WALLET_RATES every 2s until they are non-zero
    // (they may still be loading from the server when this component first mounts)
    const interval = setInterval(() => {
      const c = creationOverride ?? WALLET_RATES.CREATION_CHARGE;
      const i = installmentOverride ?? WALLET_RATES.INSTALLMENT_CHARGE;
      setRates({ creation: c, installment: i });
      if (c > 0 && i > 0) clearInterval(interval);
    }, 500);
    return () => clearInterval(interval);
  }, [creationOverride, installmentOverride]);

  return rates;
}

export default function MandateChargesCard({
  status,
  refNo,
  creationCharge: creationOverride,
  installmentRate: installmentOverride,
  retailerWalletRow,
  ledgerLink,
}: Props) {
  const active = status === "ACTIVE" || status === "PAUSED" || status === "COMPLETED";
  const { creation: creationCharge, installment: installmentRate } = useLiveRates(
    creationOverride,
    installmentOverride
  );
  const [installmentCharges, setInstallmentCharges] = useState(0);
  const [collectedCount, setCollectedCount] = useState(0);

  useEffect(() => {
    if (!refNo || !active) {
      setInstallmentCharges(0);
      setCollectedCount(0);
      return;
    }
    let cancelled = false;
    installmentService
      .getInstallments({ refNo, limit: 500 })
      .then((res) => {
        if (cancelled) return;
        const rows: Installment[] = res?.data?.installments ?? [];
        const charged = rows.filter((r) => CHARGED_STATUSES.includes(r.status));
        setInstallmentCharges(charged.reduce((s, r) => s + (r.platformCharge || 0), 0));
        setCollectedCount(charged.length);
      })
      .catch(() => {
        if (!cancelled) {
          setInstallmentCharges(0);
          setCollectedCount(0);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [refNo, active]);

  const totalCharged = creationCharge + installmentCharges;

  return (
    <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
      <h3 className="text-lg font-semibold text-ink">Charges</h3>
      <div className="mt-4 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[13px] text-ink-secondary">Creation charge</span>
          <span className="inline-flex items-center gap-2">
            <span className="text-sm font-medium text-ink">{formatINR(creationCharge)}</span>
            <Pill label="Deducted" className="bg-[#F1F5F9] text-slate-600" />
          </span>
        </div>
        {active && (
          <div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-[13px] text-ink-secondary">Installment charges</span>
              <span className="text-sm font-medium text-ink">{formatINR(installmentCharges)}</span>
            </div>
            <p className="mt-1 text-xs text-ink-secondary">
              {formatINR(installmentRate)} per successful installment
              {collectedCount > 0 ? ` · ${collectedCount} collected` : ""}
            </p>
          </div>
        )}
      </div>

      <div className="my-3.5 h-px bg-line" />

      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-ink">Total charged</span>
        <span className="text-sm font-semibold text-ink">{formatINR(totalCharged)}</span>
      </div>

      <p className="mt-3 border-t border-line pt-3 text-xs text-ink-secondary">
        Offline collections are not charged.
      </p>

      {retailerWalletRow && (
        <>
          <div className="my-3.5 h-px bg-line" />
          {retailerWalletRow}
        </>
      )}

      {ledgerLink && <div className="mt-3.5 border-t border-line pt-3">{ledgerLink}</div>}
    </div>
  );
}


